/**
 * Copy for the "someone else changed this fixture" toast (OPUS-1521).
 *
 * Pure and separate from the component so the strings are pinned by a test
 * rather than by reading JSX — these appear on an operator's screen mid-match,
 * and a wrong one ("Live disabled" when it was armed) is worse than none.
 *
 * COLOUR, per the AE design system:
 *   Live on   -> success. Green #45FFA7 — a confirmed, positive state change.
 *   Live off  -> error.   Red #FF455C — a match going off air is critical, and
 *                is the exact failure the whole all_fixtures.is_live design
 *                exists to prevent.
 *   NLE either-way -> info. Purple #9B8BFF — an informational notice; nothing is
 *                asked of the operator, and diluting the green/red Live signal
 *                with a third loud colour would make the one that matters
 *                easier to miss.
 *
 * Yellow #FCC144 is deliberately unused: it means "required action", and none of
 * these ask for one. Blue #4EC2F5 is unused: it means "you are here".
 */

/**
 * Describe what changed, from this client's point of view.
 *
 * @param {object}  args
 * @param {boolean} args.prevLive  what this client held before
 * @param {boolean} args.nextLive  what the server says now
 * @param {boolean} args.prevNle
 * @param {boolean} args.nextNle
 * @param {string|null} args.actorName  null for a service actor, or any DWS
 *   older than OPUS-1521 — render the nameless form, never "by undefined".
 * @returns {{message: string, severity: 'success'|'error'|'info'}|null}
 *   null when nothing this client cares about moved.
 */
export function describeFlagChange({
  prevLive, nextLive, prevNle, nextNle, actorName = null,
}) {
  const liveMoved = Boolean(prevLive) !== Boolean(nextLive);
  const nleMoved = Boolean(prevNle) !== Boolean(nextNle);
  if (!liveMoved && !nleMoved) return null;

  const by = actorName ? ` by ${actorName}` : '';

  // Live wins when both moved. Turning Live off cascades NLE off, so one action
  // moves both — and two toasts for one action is noise. The consequential fact
  // is the one about air.
  if (liveMoved) {
    return nextLive
      ? { message: `Live enabled${by}`, severity: 'success' }
      : { message: `Live disabled${by}`, severity: 'error' };
  }

  return nextNle
    ? { message: `NLE enabled${by}`, severity: 'info' }
    : { message: `NLE disabled${by}`, severity: 'info' };
}

export default describeFlagChange;
